/**
 * SignInActivity — dedicated page showing the current user's login history.
 *
 * Users get to this page from the tenant sidebar → "Sign-in Activity".
 * Wraps the reusable LoginHistoryTimeline component and adds a helpful
 * security tip banner + a "force-logout other sessions" button (calls the
 * existing self-scoped security endpoint).
 */
import { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, LogOut } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Ui";
import LoginHistoryTimeline from "@/components/LoginHistoryTimeline";

export default function SignInActivity() {
    const [busy, setBusy] = useState(false);
    const logoutOthers = async () => {
        if (!window.confirm("Force-logout every OTHER session on your account?\n\nYour current browser tab stays signed-in. Every other device (phone, laptop, other browsers) will need to sign in again.")) return;
        setBusy(true);
        try {
            await api.post("/security/force-logout-other-sessions");
            toast.success("Other sessions signed out. Refresh the page in ~30 seconds to see it reflected here.");
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <>
            <PageHeader title="Sign-in Activity"
                subtitle="Every sign-in to your account across all devices. If you spot anything you don't recognise, change your password and force-logout other sessions immediately."/>

            <div className="border border-amber-500/30 bg-amber-500/5 p-4 mb-6 flex items-start gap-3" data-testid="signin-activity-tip">
                <ShieldAlert className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" strokeWidth={1.6}/>
                <div className="text-xs text-muted-foreground">
                    <div className="font-medium text-amber-500 mb-1">Security tip</div>
                    A row marked <span className="text-primary font-medium">this device</span> is your current session. Any other row is a different browser or phone — normal if you signed in from your laptop earlier today. Suspicious if the location or device is unknown.
                </div>
                <button onClick={logoutOthers} disabled={busy} data-testid="signin-activity-logout-others"
                    className="ml-auto shrink-0 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 px-3 py-2 disabled:opacity-40">
                    <LogOut className="w-3 h-3"/> {busy ? "Working…" : "Force-logout other sessions"}
                </button>
            </div>

            <div className="border border-border p-6 bg-muted" data-testid="my-login-history-page">
                <LoginHistoryTimeline endpoint="/auth/login-history" limit={30} testIdPrefix="signin-activity-timeline"/>
            </div>
        </>
    );
}
